<template>
  <div class="file-tree-container">
    <section v-if="$slots['toolbar']" class="file-tree-toolbar">
      <slot name="toolbar"></slot>
    </section>
    <slot
      v-if="$slots['tree-label']"
      name="tree-label"
      classes="label help is-size-6 is-flex is-align-items-center px-4 py-1"
    >
      <!-- Suggested slot style -->
      <section
        class="label help is-size-6 is-flex is-align-items-center px-4 py-1"
      >
        File Tree
      </section>
    </slot>
    <slot name="banner" />
    <section
      class="file-tree-wrapper"
      v-bind="{ class: $attrs['tree-class'], style: $attrs['tree-style'] }"
    >
      <div v-if="isLoading" class="tree-loader">
        <span class="icon">
          <i class="fa fa-spinner fa-spin" />
        </span>
      </div>
      <tree-view
        v-else
        ref="fileTree"
        v-model="treeModel"
        class="file-tree"
        :class="{ 'is-loading': isTreeLoading }"
        v-bind="{ loadNodesAsync: isStaticTree ? null : loadTreeFunc }"
        :model-defaults="getModelDefaults"
        skin-class=""
        @tree-node-children-load="
          (target) => {
            handleEmptyLoad(target)
            saveChecked()
          }
        "
        @tree-node-checkbox-change="() => saveChecked()"
      >
        <template #expander="{ metaModel, canExpand, toggleNodeExpanded }">
          <span
            v-if="canExpand"
            class="expander-icon"
            @click="toggleNodeExpanded"
          >
            <i
              class="fa fa-xs"
              :class="
                metaModel.state.expanded
                  ? 'fa-chevron-down'
                  : 'fa-chevron-right'
              "
            />
          </span>
          <span v-else-if="metaModel.data.is_dir" class="expander-spacer" />
        </template>
        <template #loading-root>
          <div class="tree-loader">
            <span class="icon is-large">
              <i class="fa fa-spinner fa-spin fa-2x" />
            </span>
          </div>
        </template>
        <template #loading>
          <span class="icon" style="padding: 20px; margin-left: 50px">
            <i class="fa fa-spinner fa-spin" />
          </span>
        </template>
        <template #checkbox="{ metaModel, inputId, checkboxChangeHandler }">
          <div
            class="node-content"
            :class="{ 'root-node': metaModel.data.name === rootNode.name }"
          >
            <div
              v-tooltip="{
                delay: 600,
                content: metaModel.state.input.disabled
                  ? `This will be transfered as part as the selected parent dataset.
                  To select as a dataset directly please first deselect selected ancestor`
                  : '',
              }"
              class="is-flex"
              style="align-items: center; justify-content: space-between"
            >
              <div class="node-check-label">
                <label
                  :key="
                    [
                      `node-checkbox-${metaModel.data.name}-${updated}`,
                      `checked:${metaModel.state.input.value}`,
                      `expanded:${metaModel.state.expanded}`,
                    ].join('-')
                  "
                  :for="inputId"
                  :title="metaModel.title"
                  class="node-checkbox"
                  :class="{
                    disabled: disabled || metaModel.state.input.disabled,
                  }"
                >
                  <span class="checkbox-icon">
                    <template v-if="metaModel.state.input.disabled">
                      <i class="fa-solid fa-check has-text-primary fa-xs" />
                    </template>
                    <template v-else>
                      <template
                        v-if="
                          hasRealChildren(metaModel) &&
                          metaModel.state.expanded &&
                          metaModel._.state.areChildrenLoaded
                        "
                      >
                        <i
                          v-if="metaModel.state.input.value"
                          class="fa-solid fa-square-check has-text-primary"
                        />
                        <i
                          v-else
                          class="fa-regular"
                          :class="[
                            allChildrenSelected(metaModel)
                              ? 'fa-square-minus'
                              : someChildrenSelected(metaModel) ||
                                (metaModel.data.name === rootNode.name &&
                                  !canSelectRootNode)
                              ? 'fa-square-plus'
                              : 'fa-square',
                          ]"
                        />
                      </template>
                      <template v-else>
                        <i
                          v-if="metaModel.state.input.value"
                          class="fa-solid fa-square-check has-text-primary"
                        />
                        <i
                          v-else-if="
                            metaModel.data.is_dir &&
                            metaModel._.state.areChildrenLoaded &&
                            countNestedSelected(metaModel.data.relativePath) >=
                              1
                          "
                          class="fa-regular fa-square-minus"
                        />
                        <i
                          v-else
                          class="fa-regular fa-square"
                          :style="
                            metaModel.data.name === rootNode.name &&
                            !canSelectRootNode
                              ? 'opacity: 0.3'
                              : ''
                          "
                        />
                      </template>
                    </template>
                  </span>
                  <input
                    :id="inputId"
                    v-model="metaModel.state.input.value"
                    :checked="metaModel.state.input.value"
                    type="checkbox"
                    class="is-hidden"
                    :disabled="disabled || metaModel.state.input.disabled"
                    @change="
                      (event) => {
                        updateNodeState(metaModel, metaModel.state.input.value)
                        checkboxChangeHandler(event)
                      }
                    "
                  />
                </label>
                <div class="node-label-group">
                  <span
                    class="node-label"
                    :class="{
                      'has-text-weight-bold':
                        metaModel.state.input.value === true ||
                        (metaModel.data.is_dir === true &&
                          metaModel.state.input.value === false &&
                          !metaModel.state.expanded &&
                          metaModel._.state.areChildrenLoaded &&
                          countNestedSelected(metaModel.data.relativePath) >=
                            1),
                      'is-italic': metaModel.state.input.disabled,
                    }"
                  >
                    <span v-if="metaModel.data.is_dir" class="type-icon">
                      <i
                        class="fa-solid fa-sm"
                        :class="
                          metaModel.state.expanded
                            ? 'fa-folder-open'
                            : 'fa-folder'
                        "
                      />
                    </span>
                    <slot model="metaModel">
                      {{ metaModel.data[metaModel.labelProperty] }}
                    </slot>
                    <span
                      v-if="
                        metaModel.state.input.value &&
                        hasInvalidFilenameChars(metaModel.data.name)
                      "
                      v-tooltip="{
                        content: 'Filename contains invalid characters',
                        html: true,
                      }"
                      class="has-text-warning ml-1"
                    >
                      <i class="fa fa-exclamation-triangle fa-xs" />
                    </span>
                  </span>
                  <span
                    v-if="
                      metaModel.data.is_dir === true &&
                      metaModel.state.input.value === false &&
                      !metaModel.state.expanded &&
                      metaModel._.state.areChildrenLoaded &&
                      countNestedSelected(metaModel.data.relativePath) >= 1
                    "
                    class="nested-selection-info"
                  >
                    {{ countNestedSelected(metaModel.data.relativePath) }}
                    {{
                      pluralize(
                        'dataset',
                        countNestedSelected(metaModel.data.relativePath),
                      )
                    }}
                    selected
                  </span>
                </div>
                <div class="node-controls">
                  <span
                    v-if="metaModel.data.is_dir && metaModel.state.expanded"
                    class="icon is-small hoverable-icon"
                    @click="reloadFolder(metaModel)"
                  >
                    <i class="fa fa-undo-alt fa-sm" />
                  </span>
                </div>
              </div>
              <span
                v-if="
                  metaModel.data.name === rootNode.name && selectedNodes.length
                "
                class="tag has-text-weight-bold is-primary"
              >
                {{ selectedNodes.length }} selected
                {{ pluralize('dataset', selectedNodes.length) }}
              </span>
            </div>
          </div>
        </template>
        <template #text="{ metaModel }">
          <slot name="text" :model="metaModel" />
        </template>
      </tree-view>
    </section>
  </div>
</template>

<script>
import { TreeView } from '@grapoza/vue-tree'
import { uniq } from 'lodash-es'
import pluralize from 'pluralize'

import { alphaNumSort, hasInvalidFilenameChars } from '@/utils'

const IGNORED_FILENAME = ['.DS_Store']

const getIntermediaryPaths = (paths) => {
  const sep = '/'
  return paths.reduce((allPaths, path) => {
    const splitted = path.split(sep)
    let p = splitted.shift()
    allPaths.push(p)
    splitted.forEach((pUnit) => {
      p = [p, pUnit].join(sep)
      allPaths.push(p)
    })
    return allPaths
  }, [])
}

const getChildCheckboxState = (parentInput) => {
  if (!parentInput) {
    return {
      disabled: false,
      value: false,
    }
  } else {
    const parentDisabled = parentInput.disabled === true || false
    const parentChecked = parentInput.value === true || false
    return {
      disabled: parentDisabled || parentChecked,
      value: false,
    }
  }
}

const recursiveUpdateChildrenCheckboxes = (
  children,
  appliedStateSpec,
  maxDepth = 99999,
  depth = 0,
) => {
  if (depth <= maxDepth) {
    children.forEach((child) => {
      Object.assign(child.state.input, appliedStateSpec)
      const nextAppliedState = getChildCheckboxState(child?.state?.input)
      child.childMetaModels &&
        child.childMetaModels.length &&
        recursiveUpdateChildrenCheckboxes(
          child.childMetaModels,
          nextAppliedState,
          maxDepth,
          depth,
        )
    })
    depth++
  }
}

const someChildrenSelected = (node) => {
  return (
    node?.childMetaModels
      ?.map((c) => c?.state?.input?.value)
      ?.some((v) => v === true) || false
  )
}
const allChildrenSelected = (node) => {
  return (
    !node?.childMetaModels
      ?.map((c) => c?.state?.input?.value)
      ?.some((v) => v !== true) || false
  )
}

const getEmptyNode = () => ({
  uri: `empty-node-${Date.now()}`,
  name: '<empty>',
  fake: true,
})

const hasRealChildren = (node) => {
  const nodeChildren = node?.childMetaModels || []
  const fakeNodes = nodeChildren.filter((c) => c.data.fake === true) || []
  return nodeChildren.length > 0 && nodeChildren.length !== fakeNodes.length
}

export default {
  name: 'FileTree',
  components: {
    TreeView,
  },
  props: {
    rootNode: { type: Object },
    loadNode: { type: Function },
    selectedNodes: { type: Array, default: () => [] },
    staticPathList: { type: Array, default: () => [] },
    canSelectRootNode: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      fileTree: null,
      treeModel: [],
      isTreeLoading: true,
      updated: 0,
    }
  },
  computed: {
    isStaticTree() {
      return this.staticPathList?.length > 0
    },
  },
  methods: {
    pluralize,
    hasRealChildren,
    allChildrenSelected,
    someChildrenSelected,
    hasInvalidFilenameChars,
    getModelDefaults(nodeData) {
      const isDir = nodeData.is_dir === true
      const isAncestorOfSelected =
        isDir &&
        this.selectedNodes.some((p) =>
          p.startsWith(nodeData.relativePath + '/'),
        )
      const isRoot = !!(
        this.rootNode?.uri && nodeData.uri === this.rootNode.uri
      )
      const defaults = {
        idProperty: 'uri',
        labelProperty: 'name',
        expandable: isDir,
        selectable: false,
        focusable: false,
        input: nodeData.fake ? null : { type: 'checkbox' },
        state: {
          expanded: this.isStaticTree || isAncestorOfSelected || isRoot,
          input: {
            value: this.selectedNodes.includes(nodeData.relativePath) || false,
            disabled:
              nodeData._parentDisabled || nodeData._parentChecked || false,
          },
        },
        customizations: {
          classes: {
            treeViewNode: nodeData.fake ? '' : 'file-tree-node',
          },
        },
      }
      if (!this.isStaticTree) {
        const hasPreloadedChildren =
          Array.isArray(nodeData.children) && nodeData.children.length > 0
        if (!hasPreloadedChildren) {
          defaults.loadChildrenAsync = (parentMetaModel) =>
            this.loadChildren(parentMetaModel)
        }
      }
      return defaults
    },
    countNestedSelected(prefix) {
      return this.selectedNodes.filter((relativePath) =>
        relativePath.startsWith(prefix),
      ).length
    },
    async loadChildren(parentMetaModel) {
      const parentRelativePath = parentMetaModel.data.relativePath
      const children = await this.loadNode({
        relativePath: parentRelativePath,
      })
      const filtered = children
        .sort((a, b) => alphaNumSort(a.name, b.name))
        .filter((c) => !IGNORED_FILENAME.includes(c.basename))

      if (filtered.length === 0) {
        return [getEmptyNode()]
      }

      // Determine parent checkbox state for cascading
      let parentInput = parentMetaModel?.state?.input
      if (!parentInput) {
        const isParentChecked = this.selectedNodes.includes(parentRelativePath)
        parentInput = { value: isParentChecked }
      }
      const parentCheckState = getChildCheckboxState(parentInput)

      return filtered.map((c) => {
        c._parentChecked = parentCheckState.disabled || parentCheckState.value
        c._parentDisabled = parentCheckState.disabled
        // v7 requires data.children to exist as an array so spliceChildNodeList
        // can splice into it. Without this, the watchEffect sync in TreeViewNode
        // sees data.children as [] (from the ?? fallback) while childMetaModels
        // has items, and truncates them.
        if (c.is_dir && !Array.isArray(c.children)) {
          c.children = []
        }
        return c
      })
    },
    updateNodeState(metaModel, isChecked) {
      const wasChecked = !isChecked
      const isExpanded = metaModel.state.expanded === true
      const areChildrenLoaded = metaModel?._.state?.areChildrenLoaded

      const childrenCheckState =
        areChildrenLoaded &&
        metaModel?.childMetaModels?.map((c) => c.state.input.value)
      const someChildrenChecked =
        (childrenCheckState && childrenCheckState.some((v) => v === true)) ||
        false
      const allChildrenChecked =
        (childrenCheckState && !childrenCheckState?.some((v) => v !== true)) ||
        false

      let nextIsChecked = false

      const unselectableRootNode =
        metaModel.data.name === this.rootNode.name && !this.canSelectRootNode
      if (unselectableRootNode) {
        if (allChildrenChecked) {
          nextIsChecked = wasChecked
        } else if (someChildrenChecked) {
          nextIsChecked = wasChecked
        }
      } else if (
        isExpanded &&
        hasRealChildren(metaModel) &&
        (allChildrenChecked || someChildrenChecked)
      ) {
        if (allChildrenChecked) {
          nextIsChecked = wasChecked
        } else if (someChildrenChecked) {
          nextIsChecked = wasChecked
        }
      } else {
        nextIsChecked = !wasChecked
      }
      metaModel.state.input.value = nextIsChecked

      areChildrenLoaded &&
        this.updateChildrenState(
          metaModel,
          nextIsChecked,
          someChildrenChecked,
          allChildrenChecked,
        )
      this.updated++
    },
    updateChildrenState(metaModel, nextIsChecked, someSelected, allSelected) {
      const children = metaModel.childMetaModels
      const isExpanded = metaModel.state.expanded === true
      const nodeInput = metaModel.state.input
      let childNodeInputState = {
        value: !nextIsChecked,
        disabled: nextIsChecked,
      }
      if (!isExpanded) {
        childNodeInputState = getChildCheckboxState(nodeInput)
      } else if (!nextIsChecked && allSelected) {
        childNodeInputState = {
          value: false,
          disabled: false,
        }
      } else if (!nextIsChecked && someSelected && !allSelected) {
        childNodeInputState = {
          value: true,
          disabled: false,
        }
      } else if (nextIsChecked && someSelected && !allSelected) {
        childNodeInputState = {
          value: true,
          disabled: false,
        }
      }
      recursiveUpdateChildrenCheckboxes(children, childNodeInputState)
    },
    handleEmptyLoad(target) {
      const children = target.childMetaModels
      if (children.length === 0) {
        target.data.children = target.data.children || []
        target.data.children.push(getEmptyNode())
      }
    },
    reloadFolder(metaModel) {
      metaModel._.state.areChildrenLoaded = false
      metaModel.childMetaModels.splice(0)
      metaModel.data.children = []
      metaModel.state.expanded = false
      this.$nextTick(() => {
        metaModel.state.expanded = true
      })
    },
    generateStaticTree() {
      const pathList = this.staticPathList
      const rootNode = this.rootNode
      const rootPath = rootNode.relativePath

      const tree = [
        {
          id: rootPath,
          uri: rootPath,
          relativePath: rootPath,
          name: rootNode.name,
          is_dir: true,
          children: [],
        },
      ]

      const filteredPaths = pathList.filter((path) => path.startsWith(rootPath))

      let processedPaths = getIntermediaryPaths(filteredPaths)
      processedPaths = uniq(processedPaths).sort((p1, p2) => {
        return p1.split('/').length >= p2.split('/').length
      })

      processedPaths.forEach((path) => {
        if (path === rootPath) return

        const relativeParts = path.substring(rootPath.length).split('/')
        const parts = relativeParts.filter((part) => part !== '')

        let currentLevel = tree[0].children
        let currentPath = rootPath

        parts.forEach((part, index) => {
          currentPath = currentPath + '/' + part
          let existingPath = currentLevel.find((node) => node.name === part)
          const isLeaf = index === parts.length - 1

          if (!existingPath) {
            existingPath = {
              uri: currentPath,
              relativePath: currentPath,
              name: part,
              is_dir: !isLeaf,
              children: [],
            }
            currentLevel.push(existingPath)
          }

          if (isLeaf) {
            existingPath.is_dir = false
            existingPath.children = []
          } else {
            existingPath.is_dir = true
            currentLevel = existingPath.children
          }
        })
      })
      this.isTreeLoading = false
      this.updated++
      return tree
    },
    async generateTree(rootNode, pathList) {
      pathList = getIntermediaryPaths(pathList)
      pathList = uniq(pathList).sort(
        (p1, p2) => p1.split('/').length >= p2.split('/').length,
      )
      // Fetch content of all folders in path list
      // For async generateTree, we create temporary metaModel-like objects
      // to pass to loadChildren since it expects metaModel shape
      const contentPromises = pathList.map((p) => {
        const fakeMetaModel = {
          data: { relativePath: p },
          state: {
            input: {
              value: this.selectedNodes.includes(p),
              disabled: false,
            },
          },
        }
        return this.loadChildren(fakeMetaModel).then((children) => ({
          folderPath: p,
          children,
        }))
      })
      const responses = await Promise.all(contentPromises)
      // Assemble tree from info about all paths - plain data objects
      let treeData = [{ ...rootNode }]
      responses.forEach(({ folderPath, children }) => {
        let nodeData = treeData
        const pathElements = folderPath.split('/')
        const lastIndex = pathElements.length - 1
        pathElements.reduce((nodeData, name, index) => {
          const nodeIndex = nodeData.findIndex((node) => node.name === name)
          const node = nodeData[nodeIndex]
          const isLastElement = index === lastIndex
          if (isLastElement) {
            node.children =
              children && children.length ? children : [getEmptyNode()]
          }
          return node?.children || []
        }, nodeData)
      })
      return treeData
    },
    async loadTreeFunc() {
      this.isTreeLoading = true
      const qContentPaths = this.selectedNodes
        .filter((p) => p.startsWith(`${this.rootNode.name}/`))
        .map((p) => {
          var lastSep = p.lastIndexOf('/')
          return (lastSep > -1 && p.substring(0, lastSep)) || p
        })
      const paths = [this.rootNode.name, ...qContentPaths]
      const tree = await this.generateTree(this.rootNode, paths)
      this.isTreeLoading = false
      return tree
    },
    saveChecked() {
      this.$nextTick(() => {
        let checkedMetaModels = this.fileTree.getCheckedCheckboxes()
        // Map metaModels to their underlying data objects for consumers
        let checkedNodes = checkedMetaModels.map((mm) => mm.data)
        this.$emit('select-nodes', checkedNodes)
      })
    },
  },
  mounted() {
    this.fileTree = this.$refs.fileTree
    this.updated++
  },
  watch: {
    staticPathList: {
      handler() {
        if (this.isStaticTree) this.treeModel = this.generateStaticTree()
      },
      immediate: true,
    },
  },
}
</script>

<style>
.file-tree-container {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.file-tree-container section.file-tree-toolbar {
  position: sticky;
  top: 0;
  z-index: 1;
  user-select: text;
}

.file-tree-container section.file-tree-wrapper {
  display: flex;
  align-self: flex-start;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  width: 100%;
  overflow: auto;
}

.file-tree-container section.file-tree-wrapper.is-loading {
  flex-grow: 1;
  align-items: center;
}

.file-tree-container section.file-tree-wrapper .tree-loader {
  margin: 2rem auto;
}

.file-tree.grtv-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  pointer-events: auto;
  flex-grow: 1;
  /* height: 100%; */
  width: 100%;
}

.file-tree.grtv-wrapper > .grtv {
  margin: 0;
  padding: 0;
  list-style: none;
}

.file-tree.grtv-wrapper .grtvn {
  padding-left: 0;
}

.file-tree.grtv-wrapper .grtvn-children {
  padding: 0;
  list-style: none;
}

.file-tree.grtv-wrapper .grtvn.grtvn-hidden {
  display: none;
}

.file-tree.grtv-wrapper .grtvn-self {
  display: flex;
  align-items: first baseline;
}

.file-tree.grtv-wrapper .grtvn-self-sibling-drop-target {
  display: none;
}

/* Each row: [expander/spacer 20px] [checkbox 22px] [type-icon 22px] [label...] */

.file-tree.grtv-wrapper .grtvn-self .node-content {
  display: flex;
  align-items: center;
  padding: 3px 0.5em 3px 0;
}

.file-tree.grtv-wrapper .grtvn-self .node-check-label {
  display: flex;
  align-items: first baseline;
}

.file-tree.grtv-wrapper .expander-icon,
.file-tree.grtv-wrapper .expander-spacer {
  width: 20px;
  min-width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-tree.grtv-wrapper .expander-icon {
  cursor: pointer;
}

.file-tree.grtv-wrapper .node-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.file-tree.grtv-wrapper .node-checkbox .checkbox-icon {
  width: 22px;
  min-width: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-tree.grtv-wrapper .node-checkbox.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.file-tree.grtv-wrapper .node-label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-family: monospace;
  margin-left: 6px;
}

.file-tree.grtv-wrapper .node-label .type-icon {
  width: 22px;
  min-width: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 4px;
}

.file-tree.grtv-wrapper .node-label-group {
  display: flex;
  flex-direction: column;
}

.file-tree.grtv-wrapper .nested-selection-info {
  font-size: 0.75em;
  color: #3273dc;
  padding-left: 32px;
}

.file-tree.grtv-wrapper .file-tree-node .node-controls,
.file-tree.grtv-wrapper .file-tree-node .node-controls {
  margin-left: auto;
  visibility: hidden;
}

.file-tree.grtv-wrapper .file-tree-node .grtvn-self:hover .node-controls,
.file-tree.grtv-wrapper .file-tree-node .grtvn-self:hover .node-controls {
  visibility: visible;
}

.file-tree.grtv-wrapper .grtvn-children-wrapper {
  margin-left: 42px;
}

.file-tree.grtv-wrapper > ul > li.file-tree-node:last-of-type {
  padding-bottom: 1rem;
}
</style>
